import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  Video, 
  Users, 
  MessageSquare,
  Check,
  X,
  Edit,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Layout } from '../components/Layout/Layout';
import { MessageThread } from '../components/Meetings/MessageThread';
import { useMeetingStore } from '../store/meetingStore';
import { useMessageStore } from '../store/messageStore';
import { useAuthStore } from '../store/authStore';
import type { AttendeeResponse } from '../types/meetings';

export const MeetingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { 
    currentMeeting, 
    isLoading, 
    fetchMeeting, 
    updateAttendeeResponse,
    updateMeetingStatus,
    deleteMeeting 
  } = useMeetingStore();
  const { fetchOrCreateMeetingThread } = useMessageStore();
  
  const [showMessages, setShowMessages] = useState(false);
  const [thread, setThread] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (id) {
      fetchMeeting(id);
    }
  }, [id, fetchMeeting]);

  const handleBack = () => {
    navigate('/rdv');
  };

  const handleOpenMessages = async () => {
    if (!currentMeeting) return;
    
    try {
      const meetingThread = await fetchOrCreateMeetingThread(currentMeeting.id);
      setThread(meetingThread);
      setShowMessages(true);
    } catch (error) {
      console.error('Error opening messages:', error);
    }
  };

  const handleAttendeeResponse = async (response: AttendeeResponse) => {
    if (!currentMeeting || !user) return;
    
    setIsUpdating(true);
    try {
      await updateAttendeeResponse(currentMeeting.id, user.id, response);
    } catch (error) {
      console.error('Error updating response:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!currentMeeting) return;
    
    setIsUpdating(true);
    try {
      await updateMeetingStatus(currentMeeting.id, status);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteMeeting = async () => {
    if (!currentMeeting) return;
    
    if (confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) {
      try {
        await deleteMeeting(currentMeeting.id);
        navigate('/rdv');
      } catch (error) {
        console.error('Error deleting meeting:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      PLANIFIE: 'bg-orange-50 text-orange-700 border-orange-200',
      CONFIRME: 'bg-green-50 text-green-700 border-green-200',
      TERMINE: 'bg-blue-50 text-blue-700 border-blue-200',
      ANNULE: 'bg-red-50 text-red-700 border-red-200',
      REPORTE: 'bg-yellow-50 text-yellow-700 border-yellow-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      PLANIFIE: 'Planifié',
      CONFIRME: 'Confirmé',
      TERMINE: 'Terminé',
      ANNULE: 'Annulé',
      REPORTE: 'Reporté'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getResponseColor = (response: string) => {
    const colors = {
      accepted: 'text-green-600',
      declined: 'text-red-600',
      pending: 'text-orange-600'
    };
    return colors[response as keyof typeof colors] || 'text-gray-600';
  };

  const getResponseLabel = (response: string) => {
    const labels = {
      accepted: 'Accepté',
      declined: 'Refusé',
      pending: 'En attente'
    };
    return labels[response as keyof typeof labels] || response;
  };

  const currentUserAttendee = currentMeeting?.attendees?.find(
    attendee => attendee.user_id === user?.id
  );

  const isCreator = currentMeeting?.created_by === user?.id;
  const canManage = isCreator || user?.profile_type === 'ADMIN' || user?.profile_type === 'COORDINATEUR';

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!currentMeeting) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Rendez-vous non trouvé</h2>
            <p className="text-gray-600 mb-6">Le rendez-vous demandé n'existe pas ou vous n'avez pas les droits pour le consulter.</p>
            <button
              onClick={handleBack}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour aux rendez-vous
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (showMessages && thread) {
    return (
      <Layout>
        <div className="h-[calc(100vh-4rem)]">
          <MessageThread 
            thread={thread} 
            onBack={() => setShowMessages(false)}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour aux rendez-vous</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentMeeting.title}</h1>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(currentMeeting.status)}`}>
                  {getStatusLabel(currentMeeting.status)}
                </span>
                <span className="text-gray-500">
                  Créé par {currentMeeting.creator?.full_name || currentMeeting.creator?.email}
                </span>
              </div>
            </div>
            
            {canManage && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate(`/rdv/${currentMeeting.id}/edit`)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDeleteMeeting}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Meeting Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Détails du rendez-vous</h2>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {format(new Date(currentMeeting.start_date), 'EEEE dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {format(new Date(currentMeeting.start_date), 'HH:mm')} - {format(new Date(currentMeeting.end_date), 'HH:mm')}
                    </p>
                    <p className="text-sm text-gray-600">
                      Durée: {Math.round((new Date(currentMeeting.end_date).getTime() - new Date(currentMeeting.start_date).getTime()) / (1000 * 60))} minutes
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {currentMeeting.meeting_type === 'VIRTUAL' ? (
                    <>
                      <Video className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">Visioconférence</p>
                        {currentMeeting.video_link && (
                          <a
                            href={currentMeeting.video_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Rejoindre la réunion
                          </a>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">Présentiel</p>
                        <p className="text-sm text-gray-600">{currentMeeting.location || 'Lieu à définir'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {currentMeeting.description && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700">{currentMeeting.description}</p>
                </div>
              )}
            </div>

            {/* Participants */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Participants</h2>
              
              <div className="space-y-3">
                {currentMeeting.attendees?.map((attendee) => (
                  <div key={attendee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {attendee.user?.full_name?.charAt(0) || attendee.user?.email?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {attendee.user?.full_name || attendee.user?.email}
                          {attendee.is_organizer && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              Organisateur
                            </span>
                          )}
                        </p>
                        {attendee.user?.full_name && attendee.user?.email && (
                          <p className="text-sm text-gray-600">{attendee.user.email}</p>
                        )}
                      </div>
                    </div>
                    <span className={`text-sm font-medium ${getResponseColor(attendee.response)}`}>
                      {getResponseLabel(attendee.response)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={handleOpenMessages}
                  className="w-full flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Ouvrir la conversation</span>
                </button>
                
                {currentMeeting.meeting_type === 'VIRTUAL' && currentMeeting.video_link && (
                  <a
                    href={currentMeeting.video_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Video className="w-4 h-4" />
                    <span>Rejoindre la réunion</span>
                  </a>
                )}
              </div>
            </div>

            {/* Response Actions */}
            {currentUserAttendee && currentMeeting.status !== 'CANCELLED' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Votre réponse</h3>
                
                <div className="space-y-2">
                  <button
                    onClick={() => handleAttendeeResponse('accepted' as AttendeeResponse)}
                    disabled={isUpdating || currentUserAttendee.response === 'accepted'}
                    className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      currentUserAttendee.response === 'accepted'
                        ? 'bg-green-100 text-green-800 cursor-default'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                    <span>Accepter</span>
                  </button>
                  
                  <button
                    onClick={() => handleAttendeeResponse('declined' as AttendeeResponse)}
                    disabled={isUpdating || currentUserAttendee.response === 'declined'}
                    className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      currentUserAttendee.response === 'declined'
                        ? 'bg-red-100 text-red-800 cursor-default'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    <X className="w-4 h-4" />
                    <span>Refuser</span>
                  </button>
                </div>
              </div>
            )}

            {/* Management Actions */}
            {canManage && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion</h3>
                
                <div className="space-y-2">
                  {currentMeeting.status === 'PLANIFIE' && (
                    <button
                      onClick={() => handleStatusChange('CONFIRME')}
                      disabled={isUpdating}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirmer le rendez-vous
                    </button>
                  )}

                  {currentMeeting.status !== 'ANNULE' && currentMeeting.status !== 'TERMINE' && (
                    <button
                      onClick={() => handleStatusChange('ANNULE')}
                      disabled={isUpdating}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Annuler le rendez-vous
                    </button>
                  )}

                  {currentMeeting.status === 'CONFIRME' && (
                    <button
                      onClick={() => handleStatusChange('TERMINE')}
                      disabled={isUpdating}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Marquer comme terminé
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};